import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, ClipboardList, IndianRupee, FilePlus2, LogOut, Building2, ClipboardCheck, Users, FileBarChart, WifiOff, CloudUpload, LifeBuoy, History, CalendarCheck2, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { NotificationsBell } from "../NotificationsBell";
import { PinSearch } from "../PinSearch";
import { StaffAvatar, staffLabelText } from "../StaffAvatar";
import { ProfilePhotoDialog } from "../AvatarEditor";
import { api } from "../../lib/api";
import { useRoleModules, useMeState } from "../../lib/roleModules";
import { TerritoryGate, isUnmapped } from "../TerritoryGate";
import { downloadPack, downloadManual, syncAll, useOnline, useQueue, scheduleSyncStatusReport } from "../../lib/offline";
import { AssistantWidget } from "../assistant/AssistantWidget";   // Phase 171
import { RoleSwitcher } from "../RoleSwitcher";   // Phase 174: one login, several roles
import { usePolicyAccess } from "../../lib/policies";   // Phase 218: My Policies tab (+ pending count)

// Auto-log collector GPS every 3 minutes while the app is open (silent, best-effort)
export function useLocationPing(code, enabled = true) {
  useEffect(() => {
    if (!enabled || !navigator.geolocation) return;
    const ping = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          api.post(`/t/${code}/field/location`, {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          }).catch(() => {});
        },
        () => {},
        { timeout: 10000, maximumAge: 60000 }
      );
    };
    ping();
    const t = setInterval(ping, 180000);
    return () => clearInterval(t);
  }, [code]);
}

// Offline field mode: keep the device pack fresh and replay saved work when back online
function useOfflineSync(code) {
  useEffect(() => {
    // background pack refresh so forms/lists keep working without network later
    if (navigator.onLine) downloadPack(code).catch(() => {});
    // Phase 78: keep the Help & Manual copy on the device fresh for offline reading
    if (navigator.onLine) downloadManual(code).catch(() => {});
    // heartbeat: report device queue health on open + whenever the queue changes (Phase 52)
    scheduleSyncStatusReport(code);
    const onQueueChange = () => scheduleSyncStatusReport(code);
    window.addEventListener("mc-queue-changed", onQueueChange);
    return () => window.removeEventListener("mc-queue-changed", onQueueChange);
  }, [code]);
  useEffect(() => {
    const autoSync = async () => {
      const res = await syncAll(code);
      if (!res) return;
      if (res.synced > 0) toast.success(`${res.synced} saved item(s) synced to the server`);
      if (res.failed > 0) toast.error(`${res.failed} item(s) could not sync — check Field operations → Pending sync`);
    };
    if (navigator.onLine) autoSync();
    window.addEventListener("online", autoSync);
    return () => window.removeEventListener("online", autoSync);
  }, [code]);
}

export const CollectorLayout = ({ code, children }) => {
  const { auth, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [photoOpen, setPhotoOpen] = useState(false); // Phase 86: tap your avatar to change your photo
  useLocationPing(code);
  useOfflineSync(code);
  const online = useOnline();
  const queue = useQueue(code);
  const [syncBusy, setSyncBusy] = useState(false);
  const { allowed, featureAllowed } = useRoleModules(code);
  const { me, loading: meLoading } = useMeState(code);
  const isApprover = !!me?.can_approve;
  // Phase 141: an unmapped collector gets the whole-portal "No territory assigned" state —
  // no tabs, no PIN search; only Help & Manual, alerts and logout stay reachable.
  const unmapped = isUnmapped(me);
  const helpPath = `/${code}/collector/help`;
  // Phase 218: company policies assigned to me — tab appears once I am selected for any; works even when unmapped
  const { myTotal: myPolicies, myPending: myPendingPolicies } = usePolicyAccess(code);
  const policiesPath = `/${code}/collector/policies`;

  const manualSync = async () => {
    setSyncBusy(true);
    const res = await syncAll(code);
    setSyncBusy(false);
    if (res) {
      if (res.synced > 0) toast.success(`${res.synced} item(s) synced successfully`);
      if (res.failed > 0) toast.error(`${res.failed} item(s) failed — see Field operations → Pending sync`);
      if (res.synced === 0 && res.failed === 0 && res.remaining > 0) toast.info("Still offline — will retry automatically");
    }
  };

  const items = [
    { to: `/${code}/collector`, label: "Today", icon: Home, end: true, testId: "collector-nav-today", module: "today" },
    { to: `/${code}/collector/assigned`, label: "Assigned", icon: ClipboardList, testId: "collector-nav-assigned", module: "assigned", feature: "assigned_list" },
    { to: `/${code}/collector/collections`, label: "Collections", icon: IndianRupee, testId: "collector-nav-collections", module: "collections" },
    { to: `/${code}/collector/field`, label: "Field", icon: FilePlus2, testId: "collector-nav-field", module: "field" },
    { to: `/${code}/collector/reports`, label: "Reports", icon: FileBarChart, testId: "collector-nav-reports", module: "reports" },
    { to: `/${code}/collector/attendance`, label: "Attendance", icon: CalendarCheck2, testId: "collector-nav-attendance", module: "attendance" },
    ...(isApprover ? [{ to: `/${code}/collector/approvals`, label: "Approvals", icon: ClipboardCheck, testId: "collector-nav-approvals", module: "approvals" }] : []),
    ...(!me?.parent_user_id ? [{ to: `/${code}/collector/team`, label: "Team", icon: Users, testId: "collector-nav-team", module: "team" }] : []),
    { to: `/${code}/collector/notification-history`, label: "Alerts", icon: History, testId: "collector-nav-notification-history", module: "notification_history" },
  ].filter((it) => allowed(it.module) && (!it.feature || featureAllowed(it.module, it.feature)) && !unmapped);
  if (myPolicies > 0) items.push({ to: policiesPath, label: "Policies", icon: FileSignature, testId: "collector-nav-policies", badge: myPendingPolicies });   // Phase 218

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-40 bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <button type="button" className="relative shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/70"
            onClick={() => setPhotoOpen(true)} title="Change my photo" aria-label="Change my photo" data-testid="collector-my-avatar">
            <StaffAvatar code={code} userId={auth?.user?.id} name={auth?.user?.name} photoUrl={auth?.user?.photo_url ?? null} size="md" className="ring-2 ring-primary-foreground/60" />
            <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary-foreground text-primary flex items-center justify-center" aria-hidden="true">
              <Building2 className="h-2.5 w-2.5" />
            </span>
          </button>
          <div className="min-w-0">
            <p className="font-semibold text-sm font-heading truncate">{auth?.municipality?.short_name || "MunicipalConnect"}</p>
            <p className="text-[11px] opacity-80 truncate" data-testid="collector-header-role">
              {auth?.user?.custom_role_name || "Field Collector"} • {staffLabelText(auth?.user?.name, auth?.user?.employee_id)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!unmapped && <PinSearch code={code} compact />}
          <RoleSwitcher code={code} variant="onPrimary" compact />
          <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary-foreground/10"
            onClick={() => navigate(`/${code}/collector/help`)} aria-label="Help & Manual" title="Help & Manual" data-testid="collector-nav-help">
            <LifeBuoy className="h-5 w-5" />
          </Button>
          <div className="[&_button]:text-primary-foreground [&_button:hover]:bg-primary-foreground/10">
            <NotificationsBell code={code} />
          </div>
          <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary-foreground/10"
            onClick={() => { logout(); navigate("/"); }} data-testid="collector-logout-button">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>
      {!online && (
        <div className="sticky top-[57px] z-30 bg-amber-500 text-white text-xs px-4 py-1.5 flex items-center gap-2" data-testid="offline-banner">
          <WifiOff className="h-3.5 w-3.5 shrink-0" />
          <span>Offline — your field work is saved on this device{queue.length > 0 ? ` • ${queue.length} pending sync` : ""}</span>
        </div>
      )}
      {online && queue.length > 0 && (
        <div className="sticky top-[57px] z-30 bg-primary/90 text-primary-foreground text-xs px-4 py-1.5 flex items-center justify-between gap-2" data-testid="pending-sync-bar">
          <span className="flex items-center gap-2 min-w-0">
            <CloudUpload className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{queue.length} saved item(s) waiting to sync</span>
          </span>
          <button onClick={manualSync} disabled={syncBusy} className="underline font-semibold shrink-0 disabled:opacity-60"
            data-testid="layout-sync-now-button">
            {syncBusy ? "Syncing..." : "Sync now"}
          </button>
        </div>
      )}
      <main className="flex-1 pb-24 px-3 pt-4 max-w-2xl w-full mx-auto">
        <TerritoryGate me={me} loading={meLoading} role={auth?.user?.role} allowPaths={[helpPath, policiesPath]}>{children}</TerritoryGate>
      </main>
      {items.length > 0 && (
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-card border-t flex justify-around px-2 py-1.5 max-w-2xl mx-auto w-full overflow-x-auto [scrollbar-width:none]" data-testid="collector-bottom-nav">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testId}
            className={({ isActive }) =>
              `relative flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg min-w-[48px] shrink-0 ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }>
            <it.icon className="h-5 w-5" />
            <span className="text-[11px] font-medium">{it.label}</span>
            {it.badge > 0 && (
              <span className="absolute top-0.5 right-1 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold leading-4 text-center"
                data-testid={`${it.testId}-badge`} aria-label={`${it.badge} pending`}>{it.badge > 9 ? "9+" : it.badge}</span>
            )}
          </NavLink>
        ))}
      </nav>
      )}
      <ProfilePhotoDialog code={code} open={photoOpen} onOpenChange={setPhotoOpen} user={auth?.user}
        onChanged={(url) => updateUser({ photo_url: url })} />
      <AssistantWidget code={code} fabClassName="bottom-20" />
    </div>
  );
};
